import React from 'react';
import {PREPARED_CART_ICON} from '../../Constants';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import useCan, { API_KEYS_SELF, ORG_EXTENSION_MODIFY, STOREFRONT_ADMIN, TAX_CODE_MODIFY } from '../../permissions/ability';
import api, { useClearExtensionsMutation, useClearWorkspaceCacheMutation, useGetInstalledExtensionsQuery } from '../../store/Api';
import { useClearSearchMutation } from '../../store/endpoints/SavedSearchEndpoints';
import { clearSettings } from '../../store/settingsSlice';
import { EntityRequest, IGlobalSearchHit, matchRank, RANK_LABEL_SUBSTRING } from './sections';

interface IActionDescriptor {
    key: string;
    label: string;
    /** Extra terms that should find this action ("quote" → the workspace dialog). */
    aliases: string[];
    icon: string;
    /** Opens a create/details modal via CreateEntityHost… */
    request?: EntityRequest;
    /** …or runs directly (e.g. Clear cache). Exactly one of request/run is set. */
    run?: () => void;
}

/**
 * The shortcut actions the palette offers ("Create a new quote", "Add webhook", …). Each opens its
 * existing standalone create dialog/panel via CreateEntityHost — no page navigation involved.
 *
 * Gated actions are filtered out entirely (absent, not disabled) when the viewer lacks the claim.
 * useCan() is a hook, so each permission is read once up front rather than per-action in a loop.
 */
export const useSearchActions = (term: string, openEntity: (request: EntityRequest) => void): IGlobalSearchHit[] => {
    const canApiKeys = useCan(API_KEYS_SELF);
    const canExtensions = useCan(ORG_EXTENSION_MODIFY);
    const canTaxCodes = useCan(TAX_CODE_MODIFY);
    const canStorefront = useCan(STOREFRONT_ADMIN);
    const dispatch = useDispatch();
    const [clearSearchCache] = useClearSearchMutation();
    const [clearWorkspaceCache] = useClearWorkspaceCacheMutation();
    const [clearExtensionsCache] = useClearExtensionsMutation();
    const installedExtensions = useGetInstalledExtensionsQuery(undefined, { skip: !canStorefront });

    // Opportunity direct-import extensions power "Create prepared cart from opportunity" — the
    // quote-link quick flow, started from the palette instead of the PSA.
    const opportunityImportExtensions = React.useMemo(() =>
        (installedExtensions.data ?? []).filter(e => e.enabled
            && e.extensionType === 'directImport'
            && e.additionalData?.some(f => f.fieldType === 'directImportPreview')
            && `${e.id} ${e.name}`.toLowerCase().includes('opportunit')),
        [installedExtensions.data]);

    // Same sequence as UserDropdown's Clear Cache menu item.
    const clearCache = React.useCallback(() => {
        clearSearchCache();
        dispatch(clearSettings());
        clearWorkspaceCache();
        clearExtensionsCache();
        dispatch(api.util.resetApiState());
        toast.success('Cache cleared');
    }, [clearSearchCache, clearWorkspaceCache, clearExtensionsCache, dispatch]);

    return React.useMemo(() => {
        const actions: IActionDescriptor[] = [
            // "quote" and "prepared cart" are ALIASES, not a separate action: a quote is the
            // workspace → Prepare Cart flow, and a "Create a new quote" label reads badly next
            // to the quoting partners' own tooling.
            { key: 'createWorkspace', label: 'Create a workspace', aliases: ['new workspace', 'quote', 'request a quote', 'prepared cart'], icon: 'WorkItem', request: { kind: 'createWorkspace' } },
            ...(canStorefront ? opportunityImportExtensions.map((extension) => ({
                key: `preparedCartFromOpportunity-${extension.id}`,
                label: opportunityImportExtensions.length > 1
                    ? `Create prepared cart from opportunity (${extension.vendor ?? extension.source})`
                    : 'Create prepared cart from opportunity',
                aliases: ['quote from opportunity', 'import opportunity', 'opportunity import', 'psa quote'],
                icon: PREPARED_CART_ICON,
                request: { kind: 'preparedCartFromOpportunity', extensionId: extension.id } as EntityRequest,
            })) : []),
            { key: 'createContact', label: 'Create contact', aliases: ['new contact'], icon: 'AddFriend', request: { kind: 'createContact' } },
            { key: 'createCompany', label: 'Create company', aliases: ['new company'], icon: 'AddGroup', request: { kind: 'createCompany' } },
            ...(canStorefront ? [{ key: 'createStorefront', label: 'Create storefront', aliases: ['new storefront', 'store'], icon: 'Shop', request: { kind: 'createStorefront' } as EntityRequest }] : []),
            ...(canApiKeys ? [{ key: 'createApiKey', label: 'Create API key', aliases: ['apikey', 'api key'], icon: 'Permissions', request: { kind: 'createApiKey' } as EntityRequest }] : []),
            ...(canExtensions ? [
                { key: 'createWebhook', label: 'Add webhook', aliases: ['new webhook'], icon: 'PlugConnected', request: { kind: 'createWebhook' } as EntityRequest },
                { key: 'installExtension', label: 'Install extension', aliases: ['new extension', 'add extension'], icon: 'AppIconDefaultAdd', request: { kind: 'installExtension' } as EntityRequest },
            ] : []),
            ...(canTaxCodes ? [{ key: 'createTaxCode', label: 'Add tax code', aliases: ['new tax code', 'taxcode'], icon: 'Calculator', request: { kind: 'createTaxCode' } as EntityRequest }] : []),
            { key: 'clearCache', label: 'Clear cache', aliases: ['reset cache', 'cache'], icon: 'Refresh', run: clearCache },
        ];

        const hits: IGlobalSearchHit[] = [];
        actions.forEach((action, index) => {
            // Empty query: keep declaration order (workspace first), not alphabetical.
            let rank = term ? matchRank(term, action.label) : index;
            if (term && rank === undefined) {
                const aliasMatches = action.aliases.some((a) => a.includes(term) || term.includes(a));
                if (aliasMatches) rank = RANK_LABEL_SUBSTRING;
            }
            if (rank === undefined) return;
            hits.push({
                section: 'actions',
                key: action.key,
                label: action.label,
                icon: action.icon,
                rank,
                run: action.run ?? (() => action.request && openEntity(action.request)),
            });
        });
        return hits;
    }, [term, canApiKeys, canExtensions, canTaxCodes, canStorefront, opportunityImportExtensions, openEntity, clearCache]);
};
